import { useReactions } from '../queries/useReactions.js'
import { useUIStore } from '../store/uiStore.js'
import EmojiPickerButton from './EmojiPickerButton.jsx'

/**
 * ReactionBubble — live emoji reactions keyed by a label (goal or sub-goal name).
 *
 * Renders:
 *   - One pill per emoji that at least one user has reacted with, showing
 *     the emoji and a count. Clicking toggles the current user's reaction.
 *     Pills the current user has reacted with are highlighted blue.
 *   - An "add" button that opens the shared EmojiPickerButton popover to
 *     add a new reaction.
 *
 * Data flow:
 *   meta.reactions[label] = { '👍': ['Ricky', 'Zoe'], '🎉': ['Katie'] }
 *   Mutations go through /meta/reactions partial endpoint, so they never
 *   clobber any other meta slice.
 */
export default function ReactionBubble({ label }) {
  const currentUser = useUIStore((s) => s.currentUser)
  const { reactions, myEmojis, toggle, isPending } = useReactions(label)

  const entries = Object.entries(reactions).filter(
    ([, users]) => Array.isArray(users) && users.length > 0
  )

  return (
    <span
      data-tour="reaction-bubble"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        flexShrink: 0,
      }}
    >
      {entries.map(([emoji, users]) => {
        const mine = myEmojis.has(emoji)
        return (
          <button
            key={emoji}
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              if (!currentUser) return
              toggle(emoji)
            }}
            disabled={!currentUser || isPending}
            title={users.join(', ')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              background: mine ? '#dbeafe' : '#f3f4f6',
              color: mine ? '#1e40af' : 'var(--text-secondary)',
              border: `1px solid ${mine ? '#93c5fd' : '#e5e7eb'}`,
              borderRadius: 12,
              padding: '1px 6px',
              fontSize: 11,
              fontWeight: 600,
              cursor: currentUser ? 'pointer' : 'not-allowed',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
              userSelect: 'none',
              transition: 'background 0.12s',
            }}
          >
            <span style={{ fontSize: 12, lineHeight: 1 }}>{emoji}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{users.length}</span>
          </button>
        )
      })}

      {/* Add-reaction picker — reuses the shared EmojiPickerButton */}
      <EmojiPickerButton
        onInsert={(emoji) => {
          if (!currentUser) return
          toggle(emoji)
        }}
        direction="above"
        size={22}
        title="Add reaction"
      />
    </span>
  )
}
